// src/components/character/ARViewerSection.tsx
// AR viewer section with QR code and AR button for CharacterPage

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { arService, ARModelInfo } from '../../services/arService';
import { CubeTransparentIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import appeearMeLogo from '../../assets/images/appeear_me_Logo_lite.png';
import appeearMeLogoDark from '../../assets/images/appeear_me_Logo_dark.png';

interface ARViewerSectionProps {
  arModelId: string;
  characterName: string;
  className?: string;
  cardTheme?: 'dark' | 'light';
}

const ARViewerSection: React.FC<ARViewerSectionProps> = ({
  arModelId,
  characterName,
  className = '',
  cardTheme: _cardTheme = 'light',
}) => {
  const { t } = useTranslation('character');
  const [arModel, setArModel] = useState<ARModelInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect system dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    checkDarkMode();

    // Watch for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);

  const viewerUrl = arService.getViewerUrl(arModelId);

  // Fetch AR model info
  useEffect(() => {
    const fetchARModel = async () => {
      try {
        setIsLoading(true);
        const model = await arService.getARModelInfo(arModelId);
        setArModel(model);
      } catch (err) {
        console.error('Failed to fetch AR model:', err);
        setError(t('arViewer.fetchFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchARModel();
  }, [arModelId]);

  // Detect iOS for AR Quick Look
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // Handle AR button click
  const handleARClick = () => {
    if (isIOS && arModel?.usdzUrl) {
      // iOS: Use AR Quick Look with USDZ file
      const anchor = document.createElement('a');
      anchor.setAttribute('rel', 'ar');
      anchor.setAttribute('href', arModel.usdzUrl);
      anchor.click();
    } else {
      // Other devices: Open viewer URL
      window.open(viewerUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-2 animate-pulse">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !arModel) {
    return null; // Hide section if AR model not available
  }

  return (
    <>
      <div className={`bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm ${className}`}>


        <div className="flex items-center gap-4">
          {/* QR Code - Clickable to show larger */}
          <button
            onClick={() => setShowQRModal(true)}
            className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
            title={t('arViewer.enlargeQR')}
          >
            <QRCodeSVG
              value={viewerUrl}
              size={64}
              level="M"
              includeMargin={false}
            />
          </button>

          {/* AR Button */}
          <div className="flex-1">
            <button
              onClick={handleARClick}
              className="w-full py-2.5 px-4 bg-pink-600 hover:bg-pink-400 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <CubeTransparentIcon className="w-5 h-5" />
              {t('arViewer.viewInAR')}
            </button>
          </div>
        </div>

        {/* Bottom row: appeear.me logo */}
        <div className="flex justify-center mt-4">
          <a
            href="https://appeear.me"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-70 transition-opacity"
          >
            <img
              src={isDarkMode ? appeearMeLogoDark : appeearMeLogo}
              alt="appeear.me"
              className="h-5 w-auto"
            />
          </a>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShowQRModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <QrCodeIcon className="w-5 h-5 text-purple-500" />
                <h3 className="text-lg font-medium text-gray-900">
                  {t('arViewer.qrCode')}
                </h3>
              </div>
              <button
                onClick={() => setShowQRModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex justify-center mb-4">
              <div className="p-4 bg-white rounded-xl shadow-inner">
                <QRCodeSVG
                  value={viewerUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
            </div>

            <p className="text-center text-sm text-gray-600 mb-2">
              {characterName}
            </p>
            <p className="text-center text-xs text-gray-400">
              {t('arViewer.scanToViewAR')}
            </p>

            <div className="mt-4 flex gap-2">
              <button
                onClick={handleARClick}
                className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-lg"
              >
                {t('arViewer.viewOnThisDevice')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ARViewerSection;
